import React, { useState, useEffect, useRef, useMemo } from 'react';
import { preparationsApi, productsApi, depotsApi, labelsApi, stockApi } from '../lib/api';
import api, { fetchBlob } from '../lib/api';
import LabelPrintDialog from '../components/LabelPrintDialog';
import ClientSelectDialog from '../components/ClientSelectDialog';
import SearchableSelect from '../components/SearchableSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import RegulatoryDashboard from '../components/RegulatoryDashboard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  Plus, CalendarIcon, ClipboardList, Truck, Package,
  CheckCircle, Clock, ArrowRight, Eye, X,
  Upload, Loader2, Check, XCircle, Scan, AlertTriangle,
  Edit, Trash2, Save, FileText, Split, Download, Printer, Copy,
  Search, ArrowUp, ArrowDown, ArrowUpDown
} from 'lucide-react';

const PreparationsPage = () => {
  const [preparations, setPreparations] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [prepSearch, setPrepSearch] = useState('');
  const [prepSortBy, setPrepSortBy] = useState('event_date');
  const [prepSortOrder, setPrepSortOrder] = useState('desc');

  const [formData, setFormData] = useState({
    event_name: '',
    event_date: new Date(),
    client: '',
    depot_id: '',
    notes: '',
    items: [],
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');

  // Import product list state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedList, setParsedList] = useState(null);
  const [importDepotId, setImportDepotId] = useState('');
  const [importEventName, setImportEventName] = useState('');
  const [importEventDate, setImportEventDate] = useState(new Date());
  const [importClient, setImportClient] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [creatingPrep, setCreatingPrep] = useState(false);
  const [showCreateFromUnmatched, setShowCreateFromUnmatched] = useState(false);
  const [newProductForm, setNewProductForm] = useState({});
  const [addProductId, setAddProductId] = useState('');
  const [addProductQty, setAddProductQty] = useState('');
  const fileInputRef = useRef(null);
  const importDropCallbackRef = useRef(null);

  // Replace unmatched state
  const [showReplaceDialog, setShowReplaceDialog] = useState(false);
  const [replaceIndex, setReplaceIndex] = useState(null);
  const [replaceItem, setReplaceItem] = useState(null);
  const [replaceProductId, setReplaceProductId] = useState('');
  const [assemblies, setAssemblies] = useState([]);

  // Detail / Edit dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailPrep, setDetailPrep] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [editProductId, setEditProductId] = useState('');
  const [editProductQty, setEditProductQty] = useState('');
  const [saving, setSaving] = useState(false);

  // ADR analysis / split state
  const [showAdrDialog, setShowAdrDialog] = useState(false);
  const [adrAnalysis, setAdrAnalysis] = useState(null);
  const [adrLoading, setAdrLoading] = useState(false);
  const [adrForceRegime, setAdrForceRegime] = useState(null);
  const [truckContainers, setTruckContainers] = useState({});
  const [generatingDoc, setGeneratingDoc] = useState(null);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelItems, setLabelItems] = useState([]);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [clientDocType, setClientDocType] = useState('transport');
  const [pendingDocPrepId, setPendingDocPrepId] = useState(null);
  const [pendingTruckItems, setPendingTruckItems] = useState(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      
      const [prepsRes, productsRes, depotsRes, assembliesRes] = await Promise.all([
        preparationsApi.getAll(params),
        productsApi.getAll({ limit: 2000 }),
        depotsApi.getAll(),
        api.get('/assemblies').catch(() => ({ data: [] })),
      ]);

      setPreparations(prepsRes.data);
      setProducts(productsRes.data.items || []);
      setDepots(depotsRes.data);
      setAssemblies(assembliesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };


  const processImportFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('Fichier trop volumineux'); return; }

    setImporting(true);
    setParsedList(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/preparations/parse-product-list', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setParsedList(response.data);
      if (response.data.event_name) setImportEventName(response.data.event_name);
      toast.success(`${response.data.total_matched} produits reconnus`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de l\'analyse'));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportFileChange = (e) => processImportFile(e.target.files?.[0]);
  const handleImportDrop = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); processImportFile(e.dataTransfer?.files?.[0]); };
  const handleImportDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleImportDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  // Callback ref for drag/drop inside Radix Dialog
  const setImportDropZoneRef = (el) => {
    if (importDropCallbackRef.current && importDropCallbackRef.current !== el) {
      const prev = importDropCallbackRef.current;
      prev.removeEventListener('dragover', prev._onDragOver, true);
      prev.removeEventListener('dragleave', prev._onDragLeave, true);
      prev.removeEventListener('drop', prev._onDrop, true);
    }
    importDropCallbackRef.current = el;
    if (!el) return;
    el._onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
    el._onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
    el._onDrop = (e) => {
      e.preventDefault(); e.stopPropagation(); setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) processImportFile(file);
    };
    el.addEventListener('dragover', el._onDragOver, true);
    el.addEventListener('dragleave', el._onDragLeave, true);
    el.addEventListener('drop', el._onDrop, true);
  };

  const handleUpdateImportQty = (idx, val) => {
    if (!parsedList) return;
    const items = [...parsedList.matched_items];
    items[idx] = { ...items[idx], quantity: parseInt(val) || 0 };
    setParsedList({ ...parsedList, matched_items: items });
  };

  const handleRemoveImportItem = (idx) => {
    if (!parsedList) return;
    const items = parsedList.matched_items.filter((_, i) => i !== idx);
    setParsedList({ ...parsedList, matched_items: items, total_matched: items.length });
  };

  const handleOpenCreateFromUnmatched = (item, index) => {
    setNewProductForm({
      reference: item.reference || '',
      designation: item.description || item.reference || '',
      category: 'F4',
      risk_class: '1.4G',
      un_code: 'UN0336',
      caliber: item.caliber_mm ? String(item.caliber_mm) : '',
      quantity: item.quantity || 1,
      _unmatched_index: index,
    });
    setShowCreateFromUnmatched(true);
  };

  const handleCreateAndAddProduct = async () => {
    if (!newProductForm.reference || !newProductForm.designation) {
      toast.error('Référence et désignation obligatoires');
      return;
    }
    try {
      const res = await productsApi.create({
        reference: newProductForm.reference,
        designation: newProductForm.designation,
        category: newProductForm.category,
        risk_class: newProductForm.risk_class,
        un_code: newProductForm.un_code,
        caliber: newProductForm.caliber || undefined,
        // NEC + poids brut saisis en grammes, convertis en kg pour le stockage
        nec: newProductForm.nec ? parseFloat(newProductForm.nec) / 1000 : undefined,
        gross_weight: newProductForm.gross_weight ? parseFloat(newProductForm.gross_weight) / 1000 : undefined,
        manufacturer: newProductForm.manufacturer || undefined,
        homologation_number: newProductForm.homologation_number || undefined,
      });
      const created = res.data;
      const unmatchedIdx = newProductForm._unmatched_index;
      const newMatched = [...parsedList.matched_items, {
        product_id: created.id,
        reference: created.reference,
        designation: created.designation,
        quantity: newProductForm.quantity,
        risk_class: created.risk_class,
        nec_unit: created.nec || 0,
        nec_total: ((created.nec || 0) * newProductForm.quantity),
        caliber: created.caliber,
        matched: true,
      }];
      const newUnmatched = parsedList.unmatched_items.filter((_, i) => i !== unmatchedIdx);
      setParsedList({ ...parsedList, matched_items: newMatched, unmatched_items: newUnmatched, total_matched: newMatched.length, total_unmatched: newUnmatched.length });
      setShowCreateFromUnmatched(false);
      setNewProductForm({});
      // Refresh products list
      const prodRes = await productsApi.getAll({ limit: 1000 });
      setProducts(prodRes.data.items || []);
      toast.success(`${created.reference} créé et ajouté`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  const handleOpenReplace = (item, index) => {
    setReplaceItem(item);
    setReplaceIndex(index);
    setReplaceProductId('');
    setShowReplaceDialog(true);
  };

  const handleConfirmReplace = () => {
    if (!replaceProductId) {
      toast.error('Sélectionnez un produit ou une grappe');
      return;
    }
    const prod = products.find(p => p.id === replaceProductId);
    const asm = !prod ? assemblies.find(a => a.id === replaceProductId) : null;
    const target = prod || asm;
    if (!target) return;

    const newMatched = [...parsedList.matched_items, {
      product_id: target.id,
      reference: target.reference || target.ref || '',
      designation: target.designation || target.name || '',
      quantity: replaceItem.quantity || 1,
      risk_class: target.risk_class || '',
      nec_unit: target.nec || 0,
      nec_total: (target.nec || 0) * (replaceItem.quantity || 1),
      caliber: target.caliber,
      matched: true,
      is_assembly: !!asm,
    }];
    const newUnmatched = parsedList.unmatched_items.filter((_, i) => i !== replaceIndex);
    setParsedList({ ...parsedList, matched_items: newMatched, unmatched_items: newUnmatched, total_matched: newMatched.length, total_unmatched: newUnmatched.length });
    setShowReplaceDialog(false);
    toast.success(`Remplacé par ${target.reference || target.ref || target.name}`);
  };

  const handleAddManualProduct = () => {
    if (!addProductId || !addProductQty || parseInt(addProductQty) < 1) {
      toast.error('Sélectionnez un produit et une quantité');
      return;
    }
    const product = products.find(p => p.id === addProductId);
    if (!product) return;
    if (parsedList.matched_items.some(i => i.product_id === addProductId)) {
      toast.error('Ce produit est déjà dans la liste');
      return;
    }
    const qty = parseInt(addProductQty);
    const newMatched = [...parsedList.matched_items, {
      product_id: product.id,
      reference: product.reference,
      designation: product.designation,
      quantity: qty,
      risk_class: product.risk_class,
      nec_unit: product.nec || 0,
      nec_total: ((product.nec || 0) * qty),
      caliber: product.caliber,
      matched: true,
    }];
    setParsedList({ ...parsedList, matched_items: newMatched, total_matched: newMatched.length });
    setAddProductId('');
    setAddProductQty('');
    toast.success(`${product.reference} ajouté`);
  };

  const handleCreatePrepFromImport = async () => {
    if (!parsedList || !importEventName) {
      toast.error('Renseignez le nom de l\'événement');
      return;
    }
    const validItems = parsedList.matched_items.filter(i => i.quantity > 0);
    if (validItems.length === 0) { toast.error('Aucun produit valide'); return; }

    setCreatingPrep(true);
    try {
      // Determine main depot from picking sources (most used depot)
      const depotCounts = {};
      for (const item of validItems) {
        const sources = pickingSources[item.product_id] || [];
        for (const s of sources) {
          depotCounts[s.depot_id] = (depotCounts[s.depot_id] || 0) + s.qty;
        }
      }
      const mainDepotId = Object.entries(depotCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

      await preparationsApi.create({
        event_name: importEventName,
        event_date: importEventDate.toISOString(),
        client: importClient || null,
        depot_id: mainDepotId,
        notes: `Import liste produits - ${parsedList.total_matched} articles`,
        items: validItems.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          picking_sources: pickingSources[i.product_id] || [],
        })),
      });
      toast.success('Préparation créée avec succès');
      setShowImportDialog(false);
      setParsedList(null);
      setPickingSources({});
      setImportEventName('');
      setImportClient('');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    } finally {
      setCreatingPrep(false);
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct || !selectedQuantity) {
      toast.error('Sélectionnez un produit et une quantité');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    // Check if product already in list
    const existingIndex = formData.items.findIndex(i => i.product_id === selectedProduct);
    if (existingIndex >= 0) {
      const newItems = [...formData.items];
      newItems[existingIndex].quantity += parseInt(selectedQuantity);
      setFormData({ ...formData, items: newItems });
    } else {
      setFormData({
        ...formData,
        items: [...formData.items, {
          product_id: selectedProduct,
          product_reference: product.reference,
          product_designation: product.designation,
          quantity: parseInt(selectedQuantity),
        }]
      });
    }

    setSelectedProduct('');
    setSelectedQuantity('');
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleCreatePreparation = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }

    try {
      const data = {
        event_name: formData.event_name,
        event_date: formData.event_date.toISOString(),
        client: formData.client || null,
        depot_id: formData.depot_id,
        notes: formData.notes || null,
        items: formData.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity
        })),
      };

      await preparationsApi.create(data);
      toast.success('Préparation créée avec succès');
      setShowCreateDialog(false);
      fetchData();
      setFormData({
        event_name: '',
        event_date: new Date(),
        client: '',
        depot_id: '',
        notes: '',
        items: [],
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      in_progress: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      shipped: 'bg-green-500/20 text-green-400 border-green-500/30',
      returned: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    };
    const labels = {
      pending: 'En attente',
      in_progress: 'En cours',
      shipped: 'Expédié',
      returned: 'Retourné',
    };
    return (
      <Badge variant="outline" className={styles[status] || ''}>
        {labels[status] || status}
      </Badge>
    );
  };

  // --- ADR regime calculator (client-side for card badges) ---
  const ADR_COEFFS = { '1.1': 50, '1.2': 50, '1.3': 50, '1.4': 3 };

  const calcAdrRegime = (prep) => {
    if (!prep?.items?.length || !products.length) return null;
    let totalNec = 0, totalPts = 0, necHigh = 0, nec14g = 0, all14S = true;
    for (const item of prep.items) {
      const prod = products.find(p => p.id === item.product_id);
      if (!prod) continue;
      const rc = prod.risk_class || '1.4G';
      if (rc !== '1.4S') all14S = false;
      const nec = (prod.nec || 0) * (item.quantity || 0);
      const div = rc.substring(0, 3);
      const coeff = ADR_COEFFS[div] ?? 3;
      totalNec += nec;
      totalPts += nec * coeff;
      if (['1.1', '1.2', '1.3'].includes(div)) necHigh += nec;
      else if (div === '1.4' && rc !== '1.4S') nec14g += nec;
    }
    if (all14S && prep.items.length > 0) return { regime: 'LQ', label: 'LQ', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    if (totalPts <= 1000) return { regime: '1.1.3.6', label: '1.1.3.6', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' };
    // TMD 3.4.2 check
    const hasHigh = necHigh > 0;
    let tmdOk;
    if (!hasHigh) {
      tmdOk = totalNec <= 333; // Cas 1: 1.4G seul → max 333 kg
    } else {
      tmdOk = necHigh <= 100 && totalNec <= 333; // Cas 2: mixte
    }
    if (tmdOk) return { regime: '3.4.2', label: '3.4.2', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
    return { regime: 'ADR', label: 'ADR Intégral', cls: 'bg-red-500/20 text-red-400 border-red-500/30' };
  };

  // --- Detail / Edit handlers ---
  const handleOpenDetail = async (prep) => {
    setDetailPrep(prep);
    setEditMode(false);
    setEditData({
      event_name: prep.event_name || '',
      event_date: prep.event_date ? new Date(prep.event_date) : new Date(),
      client: prep.client || '',
      depot_id: prep.depot_id || '',
      notes: prep.notes || '',
      items: (prep.items || []).map(i => ({ ...i })),
    });
    setEditProductId('');
    setEditProductQty('');
    setShowDetailDialog(true);
    // Load regulatory analysis
    try {
      const res = await api.post('/documents/adr-analysis', { preparation_id: prep.id });
      setAdrAnalysis(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleEditAddItem = () => {
    if (!editProductId || !editProductQty || parseInt(editProductQty) < 1) {
      toast.error('Sélectionnez un produit et une quantité');
      return;
    }
    const product = products.find(p => p.id === editProductId);
    if (!product) return;
    const existing = editData.items.findIndex(i => i.product_id === editProductId);
    if (existing >= 0) {
      const items = [...editData.items];
      items[existing] = { ...items[existing], quantity: items[existing].quantity + parseInt(editProductQty) };
      setEditData({ ...editData, items });
    } else {
      setEditData({
        ...editData,
        items: [...editData.items, {
          product_id: editProductId,
          quantity: parseInt(editProductQty),
        }]
      });
    }
    setEditProductId('');
    setEditProductQty('');
  };

  const handleEditRemoveItem = (idx) => {
    setEditData({ ...editData, items: editData.items.filter((_, i) => i !== idx) });
  };

  const handleEditUpdateQty = (idx, val) => {
    const items = [...editData.items];
    items[idx] = { ...items[idx], quantity: parseInt(val) || 0 };
    setEditData({ ...editData, items });
  };

  const handleSaveEdit = async () => {
    if (!editData.event_name || !editData.depot_id || editData.items.length === 0) {
      toast.error('Nom, dépôt et au moins un produit requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        event_name: editData.event_name,
        event_date: editData.event_date instanceof Date ? editData.event_date.toISOString() : editData.event_date,
        client: editData.client || null,
        depot_id: editData.depot_id,
        notes: editData.notes || null,
        items: editData.items.filter(i => i.quantity > 0).map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      };
      const res = await preparationsApi.update(detailPrep.id, payload);
      toast.success('Préparation mise à jour');
      setDetailPrep(res.data);
      setEditMode(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrep = async (prepId) => {
    if (!window.confirm('Supprimer cette préparation ? Le stock réservé sera libéré.')) return;
    try {
      await preparationsApi.delete(prepId);
      toast.success('Préparation supprimée');
      setShowDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur'));
    }
  };

  const handleQuickValidate = async (prep) => {
    if (!window.confirm(`Valider et expédier "${prep.event_name}" ? Les sorties de stock seront enregistrées.`)) return;
    try {
      const items = (prep.items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity }));
      await preparationsApi.validate(prep.id, items);
      toast.success('Commande prête - Expédiée');
      setShowDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la validation'));
    }
  };

  // --- Document generation ---
  const handleDownloadBL = async (prepId, clientInfo = {}) => {
    setGeneratingDoc('bl');
    try {
      const payload = { preparation_id: prepId, ...clientInfo };
      const res = await fetchBlob('POST', '/documents/delivery-note', payload);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_sortie_stock_${prepId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Bon de Sortie Stock téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération du BL');
    }
    setGeneratingDoc(null);
  };

  // --- Recap PDF ---
  const [generatingRecap, setGeneratingRecap] = useState(false);
  const [generatingPicking, setGeneratingPicking] = useState(false);
  const [stockAvailability, setStockAvailability] = useState(null);
  // pickingSources: { [product_id]: [ {depot_id, depot_name, qty} ] }
  const [pickingSources, setPickingSources] = useState({});

  const handleDownloadRecap = async (prepId) => {
    setGeneratingRecap(true);
    try {
      const res = await preparationsApi.recapPdf(prepId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recap_preparation_${prepId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Récapitulatif téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération du récapitulatif');
    }
    setGeneratingRecap(false);
  };

  const handleDownloadPicking = async (prepId) => {
    setGeneratingPicking(true);
    try {
      const res = await preparationsApi.pickingPdf(prepId);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_preparation_${prepId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Bon de préparation téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération du bon de préparation');
    }
    setGeneratingPicking(false);
  };

  // Load stock availability when import results are ready
  useEffect(() => {
    if (parsedList?.matched_items?.length > 0) {
      const pids = parsedList.matched_items.map(i => i.product_id).filter(Boolean);
      if (pids.length > 0) {
        stockApi.availability(pids).then(res => {
          setStockAvailability(res.data);
          // Auto-assign picking sources: distribute needed qty across depots
          const auto = {};
          for (const item of parsedList.matched_items) {
            const avail = res.data?.[item.product_id];
            if (avail?.depots?.length > 0) {
              const sorted = [...avail.depots].sort((a, b) => b.available - a.available);
              const sources = [];
              let remaining = item.quantity;
              for (const d of sorted) {
                if (remaining <= 0 || d.available <= 0) break;
                const take = Math.min(remaining, d.available);
                sources.push({ depot_id: d.depot_id, depot_name: d.depot_name, qty: take });
                remaining -= take;
              }
              if (sources.length > 0) auto[item.product_id] = sources;
            }
          }
          setPickingSources(auto);
        }).catch(() => {});
      }
    } else {
      setStockAvailability(null);
      setPickingSources({});
    }
  }, [parsedList]);

  const handlePickQty = (productId, depotId, depotName, newQty) => {
    setPickingSources(prev => {
      const current = prev[productId] || [];
      const existing = current.find(s => s.depot_id === depotId);
      let updated;
      if (newQty <= 0) {
        updated = current.filter(s => s.depot_id !== depotId);
      } else if (existing) {
        updated = current.map(s => s.depot_id === depotId ? { ...s, qty: newQty } : s);
      } else {
        updated = [...current, { depot_id: depotId, depot_name: depotName, qty: newQty }];
      }
      return { ...prev, [productId]: updated };
    });
  };

  const getPickedQty = (productId, depotId) => {
    const sources = pickingSources[productId] || [];
    const s = sources.find(s => s.depot_id === depotId);
    return s ? s.qty : 0;
  };

  const getTotalPicked = (productId) => {
    const sources = pickingSources[productId] || [];
    return sources.reduce((sum, s) => sum + (s.qty || 0), 0);
  };

  const handleDownloadTransport = async (prepId, truckItems, clientInfo = {}) => {
    setGeneratingDoc('transport');
    try {
      const payload = { preparation_id: prepId, ...clientInfo };
      if (truckItems) {
        payload.items = truckItems;
        delete payload.preparation_id;
      }
      // Build containers from truckContainers state
      const containers = {};
      const prefix = truckItems ? `truck-${truckItems._truckNumber || ''}` : 'single';
      Object.entries(truckContainers).forEach(([key, val]) => {
        if (key.startsWith(prefix + '-')) {
          const rc = key.replace(prefix + '-', '');
          containers[rc] = val;
        }
      });
      if (Object.keys(containers).length > 0) {
        payload.containers = containers;
      }
      const res = await fetchBlob('POST', '/documents/transport', payload);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon_transport_${prepId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Bon de Transport téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la génération du Bon de Transport');
    }
    setGeneratingDoc(null);
  };

  // Open client selection dialog before generating docs
  const requestTransport = (prepId) => {
    setPendingDocPrepId(prepId);
    setPendingTruckItems(null);
    setClientDocType('transport');
    setShowClientDialog(true);
  };

  const requestBL = (prepId) => {
    setPendingDocPrepId(prepId);
    setClientDocType('bl');
    setShowClientDialog(true);
  };

  const handleClientConfirm = async (clientInfo) => {
    setShowClientDialog(false);
    if (clientDocType === 'bl') {
      await handleDownloadBL(pendingDocPrepId, clientInfo);
    } else {
      await handleDownloadTransport(pendingDocPrepId, pendingTruckItems, clientInfo);
    }
  };

  const handleAdrAnalysis = async (prepId, forceRegime = null) => {
    setAdrLoading(true);
    setAdrAnalysis(null);
    setTruckContainers({});
    try {
      const payload = { preparation_id: prepId };
      if (forceRegime) payload.force_regime = forceRegime;
      const res = await api.post('/documents/adr-analysis', payload);
      setAdrAnalysis(res.data);
      setShowAdrDialog(true);
    } catch (error) {
      toast.error('Erreur lors de l\'analyse ADR');
    }
    setAdrLoading(false);
  };

  const handleDownloadTruckTransport = async (truck, prepId) => {
    // Store truck items with truck number tag and open client dialog
    const items = truck.items.map(i => ({ product_id: i.product_id, quantity: i.quantity }));
    items._truckNumber = truck.truck_number;
    setPendingDocPrepId(prepId);
    setPendingTruckItems(items);
    setClientDocType('transport');
    setShowAdrDialog(false);
    setShowClientDialog(true);
  };

  const handleDuplicate = async (prepId) => {
    try {
      const res = await preparationsApi.duplicate(prepId);
      toast.success(`Préparation dupliquée → ${res.data.id}`);
      fetchPreparations();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la duplication'));
    }
  };

  const sortedPreparations = useMemo(() => {
    let items = [...preparations];
    if (prepSearch) {
      const q = prepSearch.toLowerCase();
      items = items.filter(p =>
        (p.event_name || '').toLowerCase().includes(q) ||
        (p.client || '').toLowerCase().includes(q) ||
        (p.id || '').toLowerCase().includes(q)
      );
    }
    items.sort((a, b) => {
      let valA, valB;
      switch (prepSortBy) {
        case 'event_name': valA = a.event_name || ''; valB = b.event_name || ''; break;
        case 'client': valA = a.client || ''; valB = b.client || ''; break;
        case 'event_date': valA = a.event_date || ''; valB = b.event_date || ''; break;
        case 'items_count': return prepSortOrder === 'asc' ? (a.items?.length||0)-(b.items?.length||0) : (b.items?.length||0)-(a.items?.length||0);
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        default: valA = a.event_date || ''; valB = b.event_date || '';
      }
      const cmp = valA.toString().localeCompare(valB.toString(), 'fr');
      return prepSortOrder === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [preparations, prepSearch, prepSortBy, prepSortOrder]);

  return (
    <div className="space-y-6" data-testid="preparations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Préparations</h1>
          <p className="text-zinc-400 text-sm mt-1">{sortedPreparations.length} préparation(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800 text-white">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all" className="text-white">Tous</SelectItem>
              <SelectItem value="pending" className="text-white">En attente</SelectItem>
              <SelectItem value="shipped" className="text-white">Expédiés</SelectItem>
              <SelectItem value="returned" className="text-white">Retournés</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setShowImportDialog(true)}
            className="bg-cyan-500 hover:bg-cyan-400 text-black"
            data-testid="import-product-list-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer une liste
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-preparation-button">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle Préparation
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Nouvelle Préparation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreatePreparation} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Nom de l'événement *</Label>
                    <Input
                      value={formData.event_name}
                      onChange={(e) => setFormData({...formData, event_name: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white"
                      placeholder="Ex: Fête du 14 Juillet - Commune X"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Client</Label>
                    <Input
                      value={formData.client}
                      onChange={(e) => setFormData({...formData, client: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white"
                      placeholder="Nom du client"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Date de l'événement *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left bg-zinc-950 border-zinc-800 text-white"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.event_date, 'PPP', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                        <Calendar
                          mode="single"
                          selected={formData.event_date}
                          onSelect={(date) => date && setFormData({...formData, event_date: date})}
                          locale={fr}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Dépôt source *</Label>
                    <Select 
                      value={formData.depot_id} 
                      onValueChange={(v) => setFormData({...formData, depot_id: v})}
                    >
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {depots.map(depot => (
                          <SelectItem key={depot.id} value={depot.id} className="text-white">
                            {depot.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Instructions particulières..."
                  />
                </div>

                {/* Add Products */}
                <div className="space-y-4 p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <Label className="text-zinc-300">Produits à préparer</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        value={selectedProduct}
                        onValueChange={setSelectedProduct}
                        options={products.map(p => ({ value: p.id, label: `${p.reference} — ${p.designation?.substring(0, 35) || ''}` }))}
                        placeholder="Rechercher un produit..."
                        searchPlaceholder="Tapez une référence..."
                        data-testid="prep-product-select"
                        renderOption={(opt) => {
                          const parts = opt.label.split(' — ');
                          return <><span className="font-mono text-cyan-400 mr-2 text-xs">{parts[0]}</span><span className="text-zinc-300 text-xs">{parts[1] || ''}</span></>;
                        }}
                      />
                    </div>
                    <Input
                      type="number"
                      value={selectedQuantity}
                      onChange={(e) => setSelectedQuantity(e.target.value)}
                      className="w-24 bg-zinc-900 border-zinc-700 text-white"
                      placeholder="Qté"
                      min="1"
                    />
                    <Button type="button" onClick={handleAddItem} variant="outline" className="border-zinc-700">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Items List */}
                  {formData.items.length > 0 && (
                    <div className="space-y-2 mt-4">
                      {formData.items.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-zinc-900 rounded border border-zinc-800">
                          <div className="flex-1">
                            <span className="font-mono text-cyan-400">{item.product_reference}</span>
                            <span className="text-zinc-400 ml-2">{item.product_designation?.substring(0, 30)}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-white">{item.quantity}</span>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                  <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-zinc-700">
                    Annuler
                  </Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                    Créer la préparation
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & Sort Bar */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input placeholder="Rechercher une préparation..." value={prepSearch} onChange={(e) => setPrepSearch(e.target.value)}
                className="pl-10 bg-zinc-950 border-zinc-800 text-white" data-testid="prep-search" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Tri :</span>
              <Select value={prepSortBy} onValueChange={setPrepSortBy}>
                <SelectTrigger className="w-[140px] h-8 bg-zinc-950 border-zinc-800 text-white text-xs" data-testid="prep-sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="event_date" className="text-white">Date</SelectItem>
                  <SelectItem value="event_name" className="text-white">Événement</SelectItem>
                  <SelectItem value="client" className="text-white">Client</SelectItem>
                  <SelectItem value="items_count" className="text-white">Nb produits</SelectItem>
                  <SelectItem value="status" className="text-white">Statut</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-8 border-zinc-700 text-zinc-400 hover:text-white px-2"
                onClick={() => setPrepSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} data-testid="prep-sort-order">
                {prepSortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span className="ml-1 text-xs">{prepSortOrder === 'asc' ? 'A→Z' : 'Z→A'}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preparations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <Card className="col-span-full bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center">
              <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </CardContent>
          </Card>
        ) : sortedPreparations.length === 0 ? (
          <Card className="col-span-full bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center text-zinc-500">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune préparation</p>
            </CardContent>
          </Card>
        ) : (
          sortedPreparations.map((prep) => (
            <Card key={prep.id} className="bg-zinc-900/50 border-zinc-800 card-hover">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-white truncate">{prep.event_name}</CardTitle>
                    <p className="text-sm text-zinc-400">{prep.client || 'Client non spécifié'}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getStatusBadge(prep.status)}
                    {(() => {
                      const adr = calcAdrRegime(prep);
                      if (!adr) return null;
                      return (
                        <Badge variant="outline" className={`text-[10px] ${adr.cls}`} data-testid={`adr-badge-${prep.id}`}>
                          {adr.label}
                        </Badge>
                      );
                    })()}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <CalendarIcon className="w-4 h-4" />
                    {new Date(prep.event_date).toLocaleDateString('fr-FR')}
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Package className="w-4 h-4" />
                    {prep.items?.length || 0} produits
                  </div>
                </div>

                {prep.items?.slice(0, 3).map((item, idx) => {
                  const prod = products.find(p => p.id === item.product_id);
                  return (
                    <div key={idx} className="flex items-center justify-between text-sm p-2 bg-zinc-950/50 rounded gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="font-mono text-cyan-400 text-xs">{prod?.reference || item.product_id?.slice(0, 10)}</span>
                        {prod?.designation && <span className="text-zinc-500 text-xs ml-1.5 truncate">{prod.designation.substring(0, 25)}</span>}
                      </div>
                      <span className="text-white font-mono text-xs whitespace-nowrap">{item.quantity} u.</span>
                    </div>
                  );
                })}
                {prep.items?.length > 3 && (
                  <p className="text-xs text-zinc-500 text-center">+{prep.items.length - 3} autres produits</p>
                )}

                <div className="flex flex-wrap gap-2 pt-2">
                  {prep.status === 'pending' && (
                    <>
                      <Button size="sm" className="flex-1 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30"
                        onClick={() => { handleOpenDetail(prep); setEditMode(true); }} data-testid={`edit-prep-${prep.id}`}>
                        <Edit className="w-4 h-4 mr-1" /> Modifier
                      </Button>
                      <Button size="sm" className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        onClick={() => handleQuickValidate(prep)} data-testid={`validate-prep-${prep.id}`}
                        title="Commande prête pour expédition">
                        <Truck className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {prep.status === 'shipped' && (
                    <>
                      <Button size="sm" className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30"
                        onClick={() => handleAdrAnalysis(prep.id)} data-testid={`transport-prep-${prep.id}`}
                        disabled={adrLoading}>
                        {adrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}
                        Transport
                      </Button>
                      <Button size="sm" className="bg-fuchsia-500/20 text-fuchsia-400 hover:bg-fuchsia-500/30"
                        onClick={() => requestBL(prep.id)} data-testid={`bl-prep-${prep.id}`}
                        disabled={generatingDoc === 'bl'}>
                        {generatingDoc === 'bl' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
                        BL
                      </Button>
                      <Button size="sm" className="bg-zinc-500/20 text-zinc-300 hover:bg-zinc-500/30"
                        onClick={() => handleDuplicate(prep.id)} data-testid={`duplicate-prep-${prep.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Dupli.
                      </Button>
                    </>
                  )}
                  <Button size="sm" className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                    onClick={() => handleDownloadRecap(prep.id)} data-testid={`recap-prep-${prep.id}`}
                    disabled={generatingRecap} title="Récapitulatif PDF">
                    {generatingRecap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                    Récap
                  </Button>
                  <Button size="sm" className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    onClick={() => handleDownloadPicking(prep.id)} data-testid={`picking-prep-${prep.id}`}
                    disabled={generatingPicking} title="Bon de préparation">
                    {generatingPicking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-1" />}
                    Prép.
                  </Button>
                  <Button size="sm" variant="outline" className="border-zinc-700"
                    onClick={() => handleOpenDetail(prep)} data-testid={`view-prep-${prep.id}`}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDeletePrep(prep.id)} data-testid={`delete-prep-${prep.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Detail / Edit Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={(open) => { if (!open) { setShowDetailDialog(false); setEditMode(false); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-3">
              <ClipboardList className="w-5 h-5 text-amber-400" />
              {editMode ? 'Modifier la préparation' : (detailPrep?.event_name || '')}
              {detailPrep && getStatusBadge(detailPrep.status)}
            </DialogTitle>
          </DialogHeader>

          {detailPrep && (
            <div className="space-y-6">
              {/* Info fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Événement</Label>
                  {editMode ? (
                    <Input value={editData.event_name} onChange={(e) => setEditData({...editData, event_name: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white" data-testid="edit-event-name" />
                  ) : (
                    <p className="text-white font-medium">{detailPrep.event_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Client</Label>
                  {editMode ? (
                    <Input value={editData.client} onChange={(e) => setEditData({...editData, client: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white" data-testid="edit-client" />
                  ) : (
                    <p className="text-white">{detailPrep.client || 'Non spécifié'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Date</Label>
                  {editMode ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left bg-zinc-950 border-zinc-800 text-white">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(editData.event_date, 'PPP', { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                        <Calendar mode="single" selected={editData.event_date}
                          onSelect={(date) => date && setEditData({...editData, event_date: date})} locale={fr} />
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <p className="text-white">{new Date(detailPrep.event_date).toLocaleDateString('fr-FR')}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Dépôt</Label>
                  {editMode ? (
                    <Select value={editData.depot_id} onValueChange={(v) => setEditData({...editData, depot_id: v})}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {depots.map(d => <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-white">{depots.find(d => d.id === detailPrep.depot_id)?.name || detailPrep.depot_id}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs">Notes</Label>
                  {editMode ? (
                    <Input value={editData.notes} onChange={(e) => setEditData({...editData, notes: e.target.value})}
                      className="bg-zinc-950 border-zinc-800 text-white" />
                  ) : (
                    <p className="text-zinc-400 text-sm">{detailPrep.notes || '—'}</p>
                  )}
                </div>
              </div>

              {/* Regulatory Dashboard */}
              {!editMode && adrAnalysis && (
                <RegulatoryDashboard analysis={adrAnalysis} />
              )}

              {/* Add product (edit mode only) */}
              {editMode && (
                <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                  <Label className="text-zinc-300 text-xs mb-2 block">Ajouter un produit</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        value={editProductId}
                        onValueChange={setEditProductId}
                        options={products.filter(p => !p.is_assembly).map(p => ({ value: p.id, label: `${p.reference} — ${p.designation?.substring(0, 35) || ''}` }))}
                        placeholder="Rechercher un produit..."
                        searchPlaceholder="Tapez une référence..."
                        data-testid="edit-add-product-select"
                        renderOption={(opt) => {
                          const parts = opt.label.split(' — ');
                          return <><span className="font-mono text-cyan-400 mr-2 text-xs">{parts[0]}</span><span className="text-zinc-300 text-xs">{parts[1] || ''}</span></>;
                        }}
                      />
                    </div>
                    <Input type="number" value={editProductQty} onChange={(e) => setEditProductQty(e.target.value)}
                      className="w-20 bg-zinc-900 border-zinc-700 text-white" placeholder="Qté" min="1" />
                    <Button onClick={handleEditAddItem} variant="outline" className="border-zinc-700" data-testid="edit-add-product-button">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Products table */}
              <div>
                <h4 className="text-sm font-semibold text-zinc-300 mb-3">
                  Produits ({(editMode ? editData.items : detailPrep.items)?.length || 0})
                </h4>
                <div className="max-h-[45vh] overflow-y-auto border border-zinc-800 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent sticky top-0 bg-zinc-900 z-10">
                        <TableHead className="text-zinc-400">Référence</TableHead>
                        <TableHead className="text-zinc-400">Désignation</TableHead>
                        <TableHead className="text-zinc-400">ADR</TableHead>
                        <TableHead className="text-zinc-400 text-right">Qté</TableHead>
                        {editMode && <TableHead className="text-zinc-400 w-10"></TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(editMode ? editData.items : detailPrep.items || []).map((item, idx) => {
                        const prod = products.find(p => p.id === item.product_id);
                        return (
                          <TableRow key={idx} className="border-zinc-800">
                            <TableCell className="font-mono text-cyan-400 text-xs">{prod?.reference || item.product_id?.slice(0, 10)}</TableCell>
                            <TableCell className="text-white text-sm max-w-[200px] truncate">{prod?.designation || '—'}</TableCell>
                            <TableCell>
                              {prod?.risk_class && <Badge variant="outline" className="text-xs">{prod.risk_class}</Badge>}
                            </TableCell>
                            <TableCell className="text-right">
                              {editMode ? (
                                <Input type="number" value={item.quantity} onChange={(e) => handleEditUpdateQty(idx, e.target.value)}
                                  className="w-20 h-7 bg-zinc-950 border-zinc-700 text-white text-right font-mono text-xs ml-auto" min="0" />
                              ) : (
                                <span className="font-mono text-white">{item.quantity}</span>
                              )}
                            </TableCell>
                            {editMode && (
                              <TableCell>
                                <Button variant="ghost" size="sm" className="text-red-400 h-7 w-7 p-0" onClick={() => handleEditRemoveItem(idx)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Action bar */}
              <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-zinc-800">
                {detailPrep.status === 'pending' && !editMode && (
                  <>
                    <Button onClick={() => setEditMode(true)} className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30" data-testid="detail-edit-button">
                      <Edit className="w-4 h-4 mr-2" /> Modifier
                    </Button>
                    <Button onClick={() => handleQuickValidate(detailPrep)} className="bg-green-500/20 text-green-400 hover:bg-green-500/30" data-testid="detail-validate-button">
                      <Truck className="w-4 h-4 mr-2" /> Commande prête
                    </Button>
                    <Button onClick={() => handleDuplicate(detailPrep.id)}
                      className="bg-zinc-500/10 text-zinc-300 hover:bg-zinc-500/20" data-testid="detail-duplicate-button">
                      <Copy className="w-4 h-4 mr-2" /> Dupliquer
                    </Button>
                    <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 ml-auto"
                      onClick={() => handleDeletePrep(detailPrep.id)} data-testid="detail-delete-button">
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </Button>
                  </>
                )}
                {editMode && (
                  <>
                    <Button variant="outline" className="border-zinc-700" onClick={() => setEditMode(false)}>Annuler</Button>
                    <Button onClick={handleSaveEdit} className="bg-green-500 hover:bg-green-400 text-black flex-1" disabled={saving} data-testid="save-edit-button">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer les modifications
                    </Button>
                  </>
                )}
                {detailPrep.status === 'shipped' && !editMode && (
                  <>
                    <Button onClick={() => handleAdrAnalysis(detailPrep.id)}
                      className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" disabled={adrLoading} data-testid="detail-transport-button">
                      {adrLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
                      Bon de Transport ADR
                    </Button>
                    <Button onClick={() => requestBL(detailPrep.id)}
                      className="bg-fuchsia-500/20 text-fuchsia-400 hover:bg-fuchsia-500/30" disabled={generatingDoc === 'bl'} data-testid="detail-bl-button">
                      {generatingDoc === 'bl' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Sortie Stock
                    </Button>
                    <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 ml-auto"
                      onClick={() => handleDeletePrep(detailPrep.id)} data-testid="detail-delete-shipped">
                      <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                    </Button>
                  </>
                )}
                {detailPrep.status === 'returned' && !editMode && (
                  <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDeletePrep(detailPrep.id)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                  </Button>
                )}
                {/* Recap & Picking PDFs - available for all states */}
                {!editMode && (
                  <>
                    <Button onClick={() => handleDownloadRecap(detailPrep.id)}
                      className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30"
                      disabled={generatingRecap} data-testid="detail-recap-button">
                      {generatingRecap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Récapitulatif
                    </Button>
                    <Button onClick={() => handleDownloadPicking(detailPrep.id)}
                      className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                      disabled={generatingPicking} data-testid="detail-picking-button">
                      {generatingPicking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardList className="w-4 h-4 mr-2" />}
                      Bon de préparation
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Product List Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => { setShowImportDialog(open); if (!open) { setParsedList(null); setImportEventName(''); setImportClient(''); setPickingSources({}); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Scan className="w-5 h-5 text-cyan-400" />
              Importer une liste de produits
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Upload zone */}
            {!parsedList && (
              <div
                ref={setImportDropZoneRef}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${dragOver ? 'border-cyan-400 bg-cyan-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                data-testid="import-drop-zone"
              >
                <input type="file" ref={fileInputRef} onChange={handleImportFileChange} accept=".pdf,.jpg,.jpeg,.png,.webp,.csv,.xlsx,.xls" className="hidden" />
                {importing ? (
                  <div className="space-y-3">
                    <Loader2 className="w-12 h-12 text-cyan-400 mx-auto animate-spin" />
                    <p className="text-white font-semibold">Analyse en cours...</p>
                    <p className="text-zinc-500 text-sm">L'IA lit la liste de produits</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-zinc-600 mx-auto" />
                    <p className="text-white font-semibold">Glissez ou sélectionnez la liste de produits</p>
                    <p className="text-zinc-500 text-sm">PDF, CSV, Excel, JPG, PNG</p>
                    <Button onClick={() => fileInputRef.current?.click()} className="bg-cyan-500 hover:bg-cyan-400 text-black">
                      <Upload className="w-4 h-4 mr-2" /> Sélectionner un fichier
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Parsed results */}
            {parsedList && (
              <div className="space-y-6">
                {/* Event info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Événement *</Label>
                    <Input value={importEventName} onChange={(e) => setImportEventName(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nom du spectacle" required />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Client</Label>
                    <Input value={importClient} onChange={(e) => setImportClient(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-white" placeholder="Nom du client" />
                  </div>
                </div>

                {/* Summary */}
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-3 py-1">
                    <Check className="w-4 h-4 mr-1" /> {parsedList.total_matched} reconnus
                  </Badge>
                  {parsedList.total_unmatched > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-3 py-1">
                      <XCircle className="w-4 h-4 mr-1" /> {parsedList.total_unmatched} non reconnus
                    </Badge>
                  )}
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 px-3 py-1">
                    NEC totale : {(parsedList.total_nec * 1000).toFixed(1)} g
                  </Badge>
                  <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 px-3 py-1">
                    {parsedList.total_quantity} pièces
                  </Badge>
                </div>

                {/* Matched products table */}
                {parsedList.matched_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-400 mb-3">Produits reconnus</h4>
                    <div className="max-h-[40vh] overflow-y-auto border border-zinc-800 rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent sticky top-0 bg-zinc-900 z-10">
                            <TableHead className="text-zinc-400">Référence</TableHead>
                            <TableHead className="text-zinc-400">Désignation</TableHead>
                            <TableHead className="text-zinc-400">ADR</TableHead>
                            <TableHead className="text-zinc-400 text-right">NEC</TableHead>
                            <TableHead className="text-zinc-400 text-right">Qté</TableHead>
                            <TableHead className="text-zinc-400">Stock disponible</TableHead>
                            <TableHead className="text-zinc-400 w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedList.matched_items.map((item, idx) => {
                            const avail = stockAvailability?.[item.product_id];
                            const totalAvail = avail?.total_available || 0;
                            const isShortage = totalAvail < item.quantity;
                            return (
                            <TableRow key={idx} className={`border-zinc-800 ${isShortage ? 'bg-red-500/5' : ''}`}>
                              <TableCell className="font-mono text-cyan-400 text-xs">{item.reference}</TableCell>
                              <TableCell className="text-white text-sm max-w-[150px] truncate">{item.designation}</TableCell>
                              <TableCell>
                                {item.risk_class && <Badge variant="outline" className="text-xs">{item.risk_class}</Badge>}
                              </TableCell>
                              <TableCell className="text-right font-mono text-amber-400 text-xs">{((item.nec_total || 0) * 1000).toFixed(1)} g</TableCell>
                              <TableCell className="text-right">
                                <Input type="number" value={item.quantity} onChange={(e) => handleUpdateImportQty(idx, e.target.value)}
                                  className="w-16 h-7 bg-zinc-950 border-zinc-700 text-white text-right font-mono text-xs ml-auto" min="0" />
                              </TableCell>
                              <TableCell>
                                {avail ? (
                                  <div className="space-y-0.5">
                                    {avail.depots.length > 0 ? avail.depots.map((d, di) => {
                                      const pickedQty = getPickedQty(item.product_id, d.depot_id);
                                      const isActive = pickedQty > 0;
                                      return (
                                        <div key={di}
                                          className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded transition-colors ${
                                            isActive 
                                              ? 'bg-amber-500/15 border border-amber-500/30' 
                                              : 'border border-transparent'
                                          }`}
                                        >
                                          <span className="text-zinc-400 truncate max-w-[80px] flex-shrink-0" title={d.depot_name}>{d.depot_name}</span>
                                          <span className={`font-mono text-[10px] flex-shrink-0 ${d.available > 0 ? 'text-zinc-500' : 'text-red-400'}`}>
                                            ({d.available})
                                          </span>
                                          <input
                                            type="number"
                                            min="0"
                                            max={d.available}
                                            value={pickedQty || ''}
                                            placeholder="0"
                                            onChange={(e) => handlePickQty(item.product_id, d.depot_id, d.depot_name, parseInt(e.target.value) || 0)}
                                            className={`w-12 h-5 text-right text-xs font-mono rounded border px-1 ml-auto bg-zinc-950 outline-none ${
                                              isActive ? 'border-amber-500/50 text-amber-300' : 'border-zinc-700 text-zinc-400'
                                            }`}
                                            data-testid={`pick-qty-${item.product_id}-${d.depot_id}`}
                                          />
                                        </div>
                                      );
                                    }) : (
                                      <span className="text-red-400 text-xs font-semibold">Aucun stock</span>
                                    )}
                                    {(() => {
                                      const totalPicked = getTotalPicked(item.product_id);
                                      const diff = item.quantity - totalPicked;
                                      if (totalAvail === 0) return null;
                                      if (diff > 0) return <span className="text-red-400 text-[10px] font-semibold">Manque {diff} à affecter</span>;
                                      if (diff < 0) return <span className="text-amber-400 text-[10px]">Surplus de {-diff}</span>;
                                      return <span className="text-green-400 text-[10px]">Affecté</span>;
                                    })()}
                                  </div>
                                ) : (
                                  <span className="text-zinc-600 text-xs">Chargement...</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" className="text-red-400 h-7 w-7 p-0" onClick={() => handleRemoveImportItem(idx)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Unmatched */}
                {parsedList.unmatched_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-400 mb-3">Non reconnus ({parsedList.unmatched_items.length})</h4>
                    <div className="space-y-1 max-h-[30vh] overflow-y-auto">
                      {parsedList.unmatched_items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 rounded border border-red-500/20 text-sm">
                          <XCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                          <span className="font-mono text-red-400">{item.reference}</span>
                          <span className="text-zinc-500 text-xs truncate flex-1">{item.description}</span>
                          <span className="text-zinc-500 text-xs">x{item.quantity}</span>
                          <Button size="sm" className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 h-6 text-xs px-2"
                            onClick={() => handleOpenReplace(item, idx)} data-testid={`replace-unmatched-${idx}`}>
                            <ArrowRight className="w-3 h-3 mr-1" /> Remplacer
                          </Button>
                          <Button size="sm" className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 h-6 text-xs px-2"
                            onClick={() => handleOpenCreateFromUnmatched(item, idx)}>
                            <Plus className="w-3 h-3 mr-1" /> Créer
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add manual product */}
                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <h4 className="text-sm font-semibold text-zinc-300 mb-3">Ajouter un produit manuellement</h4>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <SearchableSelect
                        value={addProductId}
                        onValueChange={setAddProductId}
                        options={products.filter(p => !p.is_assembly).map(p => ({ value: p.id, label: `${p.reference} — ${p.designation?.substring(0, 35) || ''}` }))}
                        placeholder="Rechercher un produit..."
                        searchPlaceholder="Tapez une référence..."
                        data-testid="add-manual-product-select"
                        renderOption={(opt) => {
                          const parts = opt.label.split(' — ');
                          return <><span className="font-mono text-cyan-400 mr-2 text-xs">{parts[0]}</span><span className="text-zinc-300 text-xs">{parts[1] || ''}</span></>;
                        }}
                      />
                    </div>
                    <Input type="number" value={addProductQty} onChange={(e) => setAddProductQty(e.target.value)}
                      className="w-20 bg-zinc-900 border-zinc-700 text-white" placeholder="Qté" min="1" />
                    <Button onClick={handleAddManualProduct} variant="outline" className="border-zinc-700" data-testid="add-manual-product-button">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-zinc-800">
                  <Button variant="outline" className="border-zinc-700" onClick={() => setParsedList(null)}>
                    <Upload className="w-4 h-4 mr-2" /> Réanalyser
                  </Button>
                  <Button
                    onClick={handleCreatePrepFromImport}
                    className="flex-1 bg-green-500 hover:bg-green-400 text-black font-semibold"
                    disabled={creatingPrep || !importEventName || parsedList.matched_items.length === 0}
                    data-testid="validate-import-button"
                  >
                    {creatingPrep ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Créer la préparation ({parsedList.matched_items.filter(i => i.quantity > 0).length} produits)
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Unmatched Product Modal */}
      <Dialog open={showReplaceDialog} onOpenChange={setShowReplaceDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <ArrowRight className="w-5 h-5 text-cyan-400" />
              Remplacer par un produit/grappe existant
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {replaceItem && (
              <div className="p-3 bg-red-500/10 rounded border border-red-500/20">
                <p className="text-xs text-zinc-400">Produit non reconnu :</p>
                <p className="text-red-400 font-mono text-sm">{replaceItem.reference} — {replaceItem.description} (x{replaceItem.quantity})</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-zinc-400 text-xs">Rechercher un produit ou une grappe</Label>
              <SearchableSelect
                value={replaceProductId}
                onValueChange={setReplaceProductId}
                options={[
                  ...products.filter(p => !p.is_assembly).map(p => ({ value: p.id, label: `${p.reference} — ${(p.designation || '').substring(0, 35)}` })),
                  ...assemblies.map(a => ({ value: a.id, label: `[Grappe] ${a.ref || a.reference} — ${(a.name || a.designation || '').substring(0, 35)}` })),
                ]}
                placeholder="Sélectionner..."
                searchPlaceholder="Tapez une référence..."
                data-testid="replace-product-select"
                renderOption={(opt) => {
                  if (opt.label.startsWith('[Grappe]')) {
                    const text = opt.label.replace('[Grappe] ', '');
                    const parts = text.split(' — ');
                    return <><span className="font-mono text-amber-400 mr-2 text-xs">{parts[0]}</span><span className="text-zinc-300 text-xs">{parts[1] || ''}</span></>;
                  }
                  const parts = opt.label.split(' — ');
                  return <><span className="font-mono text-cyan-400 mr-2 text-xs">{parts[0]}</span><span className="text-zinc-300 text-xs">{parts[1] || ''}</span></>;
                }}
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
              <Button variant="outline" className="border-zinc-700" onClick={() => setShowReplaceDialog(false)}>Annuler</Button>
              <Button className="bg-cyan-500 hover:bg-cyan-400 text-black" onClick={handleConfirmReplace} data-testid="confirm-replace-button">
                <ArrowRight className="w-4 h-4 mr-2" /> Remplacer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Product from Unmatched Modal */}
      <Dialog open={showCreateFromUnmatched} onOpenChange={setShowCreateFromUnmatched}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Créer un nouveau produit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Référence *</Label>
                <Input value={newProductForm.reference || ''} onChange={(e) => setNewProductForm({...newProductForm, reference: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="create-product-reference" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Désignation *</Label>
                <Input value={newProductForm.designation || ''} onChange={(e) => setNewProductForm({...newProductForm, designation: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" data-testid="create-product-designation" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Catégorie</Label>
                <Select value={newProductForm.category || 'F4'} onValueChange={(v) => setNewProductForm({...newProductForm, category: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {['F1','F2','F3','F4','T1','T2','P1','P2'].map(c => (
                      <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Classe ADR</Label>
                <Select value={newProductForm.risk_class || '1.4G'} onValueChange={(v) => setNewProductForm({...newProductForm, risk_class: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {['1.1G','1.2G','1.3G','1.4G','1.4S'].map(c => (
                      <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Code ONU</Label>
                <Input value={newProductForm.un_code || ''} onChange={(e) => setNewProductForm({...newProductForm, un_code: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" placeholder="UN0336" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Calibre (mm)</Label>
                <Input type="number" value={newProductForm.caliber || ''} onChange={(e) => setNewProductForm({...newProductForm, caliber: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">NEC (g)</Label>
                <Input type="number" step="0.001" value={newProductForm.nec || ''} onChange={(e) => setNewProductForm({...newProductForm, nec: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">Quantité</Label>
                <Input type="number" value={newProductForm.quantity || ''} onChange={(e) => setNewProductForm({...newProductForm, quantity: parseInt(e.target.value) || 0})}
                  className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" min="1" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t border-zinc-800">
              <Button variant="outline" className="border-zinc-700" onClick={() => setShowCreateFromUnmatched(false)}>Annuler</Button>
              <Button className="bg-amber-500 hover:bg-amber-400 text-black" onClick={handleCreateAndAddProduct} data-testid="confirm-create-product">
                <Plus className="w-4 h-4 mr-2" /> Créer et ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Select Dialog (before generating Transport/BL) */}
      <ClientSelectDialog
        open={showClientDialog}
        onOpenChange={setShowClientDialog}
        onConfirm={handleClientConfirm}
        docType={clientDocType}
        loading={generatingDoc !== null}
      />

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={showLabelDialog}
        onOpenChange={setShowLabelDialog}
        items={labelItems}
        products={products}
      />

      {/* ADR Split Dialog */}
      <Dialog open={showAdrDialog} onOpenChange={setShowAdrDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Split className="w-5 h-5 text-red-400" />
              Analyse réglementaire — Division du chargement
            </DialogTitle>
          </DialogHeader>

          {adrAnalysis && (
            <div className="space-y-5">
              {/* Regulatory Dashboard with gauges */}
              <RegulatoryDashboard analysis={adrAnalysis} />

              {/* Regime selector — premium cards */}
              <div className="grid grid-cols-2 gap-3">
                <button type="button"
                  onClick={() => { setAdrForceRegime('TMD_342'); handleAdrAnalysis(adrAnalysis.preparation_id, null); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    !adrForceRegime || adrForceRegime === 'TMD_342'
                      ? 'border-amber-500 bg-amber-500/10 shadow-lg shadow-amber-500/5'
                      : 'border-zinc-800 bg-zinc-950 hover:border-zinc-600'
                  }`} data-testid="regime-tmd">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      !adrForceRegime || adrForceRegime === 'TMD_342' ? 'bg-amber-500/20' : 'bg-zinc-800'
                    }`}>
                      <Truck className={`w-5 h-5 ${!adrForceRegime || adrForceRegime === 'TMD_342' ? 'text-amber-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${!adrForceRegime || adrForceRegime === 'TMD_342' ? 'text-amber-400' : 'text-zinc-400'}`}>TMD 3.4.2</p>
                      <p className="text-zinc-500 text-xs">Votre entreprise</p>
                    </div>
                    {(!adrForceRegime || adrForceRegime === 'TMD_342') && <Check className="w-5 h-5 text-amber-400 ml-auto" />}
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">Seuils NEC élevés, transport simplifié. Idéal si vous transportez vous-même.</p>
                </button>
                <button type="button"
                  onClick={() => { setAdrForceRegime('ADR_1136'); handleAdrAnalysis(adrAnalysis.preparation_id, 'ADR_1136'); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    adrForceRegime === 'ADR_1136'
                      ? 'border-red-500 bg-red-500/10 shadow-lg shadow-red-500/5'
                      : 'border-zinc-800 bg-zinc-950 hover:border-zinc-600'
                  }`} data-testid="regime-adr">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      adrForceRegime === 'ADR_1136' ? 'bg-red-500/20' : 'bg-zinc-800'
                    }`}>
                      <AlertTriangle className={`w-5 h-5 ${adrForceRegime === 'ADR_1136' ? 'text-red-400' : 'text-zinc-500'}`} />
                    </div>
                    <div>
                      <p className={`font-semibold text-sm ${adrForceRegime === 'ADR_1136' ? 'text-red-400' : 'text-zinc-400'}`}>ADR 1.1.3.6</p>
                      <p className="text-zinc-500 text-xs">Transport client</p>
                    </div>
                    {adrForceRegime === 'ADR_1136' && <Check className="w-5 h-5 text-red-400 ml-auto" />}
                  </div>
                  <p className="text-zinc-500 text-[11px] leading-relaxed">Seuils par points (1000 pts max). Pour les clients sans agrément TMD.</p>
                </button>
              </div>

              {/* Option 1: Single vehicle */}
              <div className="rounded-xl border border-zinc-800 overflow-hidden">
                <div className="px-5 py-3 bg-zinc-900/80 border-b border-zinc-800 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-white font-bold text-sm">1</div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">Transport complet</h4>
                      <p className="text-zinc-500 text-xs">
                        {adrAnalysis.regime === 'ADR_INTEGRAL' ? 'ADR Intégral — Placardage, formation conducteur, extincteurs' : 'TMD Simplifié — Document de transport requis'}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${adrAnalysis.regime === 'ADR_INTEGRAL' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}`}>
                    {adrAnalysis.regime === 'ADR_INTEGRAL' ? 'ADR' : 'TMD 3.4.2'}
                  </Badge>
                </div>

                {/* Containers per class */}
                <div className="p-4 space-y-3">
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Colisage par classe de dangerosité</p>
                  <div className="space-y-2">
                    {(() => {
                      const classes = {};
                      (adrAnalysis.items || []).forEach(i => {
                        const rc = i.risk_class || '1.4G';
                        if (!classes[rc]) classes[rc] = { qty: 0, nec: 0 };
                        classes[rc].qty += i.quantity;
                        classes[rc].nec += i.nec_total || 0;
                      });
                      const entries = Object.entries(classes).sort();
                      const classColors = { '1.1G': 'bg-red-500/20 text-red-400 border-red-500/30', '1.2G': 'bg-red-500/15 text-red-300 border-red-500/25', '1.3G': 'bg-orange-500/20 text-orange-400 border-orange-500/30', '1.4G': 'bg-amber-500/20 text-amber-400 border-amber-500/30', '1.4S': 'bg-green-500/20 text-green-400 border-green-500/30' };
                      return (
                        <>
                          {entries.map(([rc, info]) => (
                            <div key={rc} className="flex items-center gap-3 p-2.5 bg-zinc-950 rounded-lg border border-zinc-800">
                              <Badge className={`text-xs px-2.5 py-0.5 font-mono ${classColors[rc] || 'bg-zinc-800 text-zinc-300 border-zinc-700'}`}>{rc}</Badge>
                              <div className="flex-1 min-w-0">
                                <span className="text-zinc-300 text-xs">{info.qty} articles</span>
                                <span className="text-zinc-600 text-xs mx-1.5">/</span>
                                <span className="text-amber-400/70 text-xs font-mono">{(info.nec * 1000).toFixed(1)} g NEC</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-zinc-500 text-xs">Palettes</span>
                                  <input type="number" min="0"
                                    value={truckContainers[`single-${rc}`]?.palettes || ''}
                                    onChange={(e) => setTruckContainers(prev => ({...prev, [`single-${rc}`]: {...(prev[`single-${rc}`]||{}), palettes: parseInt(e.target.value)||0}}))}
                                    className="w-16 h-7 bg-zinc-900 border border-zinc-700 rounded-md px-2 text-xs text-white text-center focus:border-amber-500/50 outline-none transition-colors" placeholder="0" />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="text-zinc-500 text-xs">Cartons</span>
                                  <input type="number" min="0"
                                    value={truckContainers[`single-${rc}`]?.cartons || ''}
                                    onChange={(e) => setTruckContainers(prev => ({...prev, [`single-${rc}`]: {...(prev[`single-${rc}`]||{}), cartons: parseInt(e.target.value)||0}}))}
                                    className="w-16 h-7 bg-zinc-900 border border-zinc-700 rounded-md px-2 text-xs text-white text-center focus:border-amber-500/50 outline-none transition-colors" placeholder="0" />
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* Total row */}
                          {(() => {
                            let totalPal = 0, totalCart = 0;
                            entries.forEach(([rc]) => {
                              totalPal += truckContainers[`single-${rc}`]?.palettes || 0;
                              totalCart += truckContainers[`single-${rc}`]?.cartons || 0;
                            });
                            return (totalPal > 0 || totalCart > 0) ? (
                              <div className="flex items-center justify-end gap-4 pt-2 border-t border-zinc-800">
                                <span className="text-zinc-400 text-xs font-semibold">Total colis :</span>
                                {totalPal > 0 && <span className="text-amber-400 text-xs font-mono">{totalPal} palette{totalPal > 1 ? 's' : ''}</span>}
                                {totalCart > 0 && <span className="text-amber-400 text-xs font-mono">{totalCart} carton{totalCart > 1 ? 's' : ''}</span>}
                                <span className="text-white text-xs font-bold">{totalPal + totalCart} colis</span>
                              </div>
                            ) : null;
                          })()}
                        </>
                      );
                    })()}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-3 border-t border-zinc-800">
                    <Button onClick={() => { requestTransport(adrAnalysis.preparation_id); setShowAdrDialog(false); }}
                      className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                      disabled={generatingDoc === 'transport'} data-testid="adr-full-download">
                      {generatingDoc === 'transport' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      {adrAnalysis.regime === 'ADR_INTEGRAL' ? 'Bon ADR Intégral' : 'Bon TMD 3.4.2'}
                    </Button>
                    {adrAnalysis.regime !== 'ADR_INTEGRAL' && (
                      <Button onClick={() => { requestTransport(adrAnalysis.preparation_id); setShowAdrDialog(false); }}
                        variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        disabled={generatingDoc === 'transport'} data-testid="adr-complet-download">
                        {generatingDoc === 'transport' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                        Bon ADR Complet
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Option 2: Split into multiple vehicles */}
              {adrAnalysis.split_suggestion && (
                <div className="rounded-xl border border-green-500/30 overflow-hidden">
                  <div className="px-5 py-3 bg-green-500/5 border-b border-green-500/20 flex items-center gap-3">
                    <Split className="w-5 h-5 text-green-400" />
                    <div>
                      <h4 className="text-green-400 font-semibold text-sm">
                        Division en {adrAnalysis.split_suggestion.num_trucks} véhicules
                      </h4>
                      <p className="text-zinc-500 text-xs">
                        {adrAnalysis.split_suggestion.target_regime === '1.1.3.6'
                          ? 'Chaque véhicule respecte ≤ 1000 points ADR'
                          : `Classes hautes ≤ ${adrAnalysis.regulatory?.tmd_342?.threshold_high || 100} kg, NEC globale ≤ ${adrAnalysis.regulatory?.tmd_342?.threshold_global || 333} kg`}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
                    {adrAnalysis.split_suggestion.trucks.map((truck) => {
                      const classColors = { '1.1G': 'bg-red-500/20 text-red-400 border-red-500/30', '1.2G': 'bg-red-500/15 text-red-300 border-red-500/25', '1.3G': 'bg-orange-500/20 text-orange-400 border-orange-500/30', '1.4G': 'bg-amber-500/20 text-amber-400 border-amber-500/30', '1.4S': 'bg-green-500/20 text-green-400 border-green-500/30' };
                      const truckClasses = {};
                      (truck.items || []).forEach(i => {
                        const rc = i.risk_class || '1.4G';
                        if (!truckClasses[rc]) truckClasses[rc] = { qty: 0, nec: 0 };
                        truckClasses[rc].qty += i.quantity;
                        truckClasses[rc].nec += i.nec_total || 0;
                      });
                      const ptsMax = adrAnalysis.split_suggestion.target_regime === '1.1.3.6' ? 1000 : (adrAnalysis.regulatory?.tmd_342?.threshold_global || 333);
                      const ptsVal = adrAnalysis.split_suggestion.target_regime === '1.1.3.6' ? (truck.total_points || 0) : (truck.total_nec || 0);
                      const ptsPct = Math.min(100, (ptsVal / ptsMax) * 100);

                      return (
                        <div key={truck.truck_number} className="rounded-lg border border-zinc-800 overflow-hidden bg-zinc-950">
                          {/* Truck header */}
                          <div className="px-4 py-2.5 bg-zinc-900/50 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-lg bg-zinc-800 flex items-center justify-center text-white font-bold text-xs">{truck.truck_number}</div>
                              <div>
                                <span className="text-white font-semibold text-sm">Véhicule {truck.truck_number}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-zinc-500 text-[11px]">{truck.total_items} articles</span>
                                  <span className="text-amber-400 text-[11px] font-mono">NEC {(truck.total_nec * 1000).toFixed(1)} g</span>
                                  <Badge variant="outline" className={`text-[9px] py-0 ${truck.tmd_conforme ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-red-500/10 text-red-400 border-red-500/30'}`}>
                                    {truck.tmd_conforme ? 'TMD' : 'ADR'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            <Button size="sm" onClick={() => handleDownloadTruckTransport(truck, adrAnalysis.preparation_id)}
                              className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 h-8"
                              disabled={generatingDoc === `truck-${truck.truck_number}`} data-testid={`download-truck-${truck.truck_number}`}>
                              {generatingDoc === `truck-${truck.truck_number}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
                              PDF
                            </Button>
                          </div>
                          {/* Progress bar */}
                          <div className="px-4 pt-2 pb-1">
                            <div className="flex items-center justify-between text-[10px] mb-1">
                              <span className="text-zinc-600">{adrAnalysis.split_suggestion.target_regime === '1.1.3.6' ? 'Points ADR' : 'NEC'}</span>
                              <span className={`font-mono ${ptsPct >= 95 ? 'text-red-400' : ptsPct >= 80 ? 'text-amber-400' : 'text-green-400'}`}>
                                {ptsVal.toFixed(1)} / {ptsMax}
                              </span>
                            </div>
                            <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${ptsPct >= 95 ? 'bg-red-500' : ptsPct >= 80 ? 'bg-amber-500' : 'bg-green-500'}`} style={{width: `${ptsPct}%`}} />
                            </div>
                          </div>
                          {/* Per-class containers */}
                          <div className="px-4 pb-3 pt-2 space-y-1.5">
                            {Object.entries(truckClasses).sort().map(([rc, info]) => (
                              <div key={rc} className="flex items-center gap-2">
                                <Badge className={`text-[10px] px-2 py-0 font-mono ${classColors[rc] || 'bg-zinc-800 text-zinc-300 border-zinc-700'}`}>{rc}</Badge>
                                <span className="text-zinc-500 text-[10px] w-14 flex-shrink-0">{info.qty} art.</span>
                                <div className="flex items-center gap-1.5 ml-auto">
                                  <span className="text-zinc-600 text-[10px]">Palettes</span>
                                  <input type="number" min="0"
                                    value={truckContainers[`truck-${truck.truck_number}-${rc}`]?.palettes || ''}
                                    onChange={(e) => setTruckContainers(prev => ({...prev, [`truck-${truck.truck_number}-${rc}`]: {...(prev[`truck-${truck.truck_number}-${rc}`]||{}), palettes: parseInt(e.target.value)||0}}))}
                                    className="w-14 h-6 bg-zinc-900 border border-zinc-700 rounded px-1.5 text-[11px] text-white text-center focus:border-amber-500/50 outline-none" placeholder="0" />
                                  <span className="text-zinc-600 text-[10px] ml-1">Cartons</span>
                                  <input type="number" min="0"
                                    value={truckContainers[`truck-${truck.truck_number}-${rc}`]?.cartons || ''}
                                    onChange={(e) => setTruckContainers(prev => ({...prev, [`truck-${truck.truck_number}-${rc}`]: {...(prev[`truck-${truck.truck_number}-${rc}`]||{}), cartons: parseInt(e.target.value)||0}}))}
                                    className="w-14 h-6 bg-zinc-900 border border-zinc-700 rounded px-1.5 text-[11px] text-white text-center focus:border-amber-500/50 outline-none" placeholder="0" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!adrAnalysis.split_suggestion && adrAnalysis.regime !== 'ADR_INTEGRAL' && !adrForceRegime && (
                <div className="p-4 bg-zinc-950 border border-zinc-700 rounded-xl text-center">
                  <p className="text-zinc-500 text-sm">Le chargement est conforme — aucune division nécessaire.</p>
                  <p className="text-zinc-600 text-xs mt-1">Sélectionnez "ADR 1.1.3.6" ci-dessus si votre client l'exige.</p>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" className="border-zinc-700" onClick={() => setShowAdrDialog(false)}>Fermer</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PreparationsPage;
